import {
  BulbOutlined,
  DeleteOutlined,
  FireOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Input,
  message,
  Modal,
  Pagination,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../api";
import { fetchConfig, updateConfig } from "../slice";

const { Title, Text } = Typography;

export default function HotKeywords() {
  const dispatch = useDispatch();
  const { config, isLoading } = useSelector((state) => state.foodConfig);
  const [keywords, setKeywords] = useState("");
  const [searchLogs, setSearchLogs] = useState([]);
  const [searchLogPagination, setSearchLogPagination] = useState({
    current: 1,
    pageSize: 200,
    total: 0,
  });
  const [isLoadingSearchLogs, setIsLoadingSearchLogs] = useState(false);

  // Search Suggestions states
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchSuggestionsPagination, setSearchSuggestionsPagination] =
    useState({
      current: 1,
      pageSize: 200,
      total: 0,
    });
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [newKeyword, setNewKeyword] = useState("");
  const [isAddingKeyword, setIsAddingKeyword] = useState(false);

  // Edit states
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isUpdatingKeyword, setIsUpdatingKeyword] = useState(false);
  const [creatingFromLog, setCreatingFromLog] = useState(null);

  // Create from log states
  const [editingFromLogId, setEditingFromLogId] = useState(null);
  const [editingFromLogValue, setEditingFromLogValue] = useState("");
  const [isCreatingFromLog, setIsCreatingFromLog] = useState(false);

  useEffect(() => {
    dispatch(fetchConfig());
    fetchSearchLogs();
    fetchSearchSuggestions();
  }, [dispatch]);

  const fetchSearchLogs = async (pagination = searchLogPagination) => {
    try {
      setIsLoadingSearchLogs(true);
      const response = await api.getSearchLog({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });

      if (response.data) {
        setSearchLogs(response.data);
        setSearchLogPagination({
          current: response.meta.current,
          pageSize: response.meta.pageSize,
          total: response.meta.total,
        });
      }
    } catch (error) {
      console.error("Error fetching search logs:", error);
    } finally {
      setIsLoadingSearchLogs(false);
    }
  };

  const fetchSearchSuggestions = async (
    pagination = searchSuggestionsPagination
  ) => {
    try {
      setIsLoadingSuggestions(true);
      const response = await api.getSearchSuggestions({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });

      if (response.data) {
        setSearchSuggestions(response.data);
        setSearchSuggestionsPagination({
          current: response.meta.current,
          pageSize: response.meta.pageSize,
          total: response.meta.total,
        });
      }
    } catch (error) {
      console.error("Error fetching search suggestions:", error);
      message.error("Lỗi khi tải danh sách từ khóa gợi ý");
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    const hotKeywords = config.hotKeywords || [];
    setKeywords(hotKeywords.join(", "));
  }, [config]);

  const onChangeKeywords = (e) => {
    setKeywords(e.target.value);
  };

  const onSubmit = () => {
    const kws = keywords
      .split(",")
      .map((e) => e.trim())
      .filter((e) => !!e);
    dispatch(updateConfig({ hotKeywords: kws }));
  };

  const onSearchLogPageChange = (page, pageSize) => {
    const newPagination = {
      current: page,
      pageSize: pageSize,
      total: searchLogPagination.total,
    };
    setSearchLogPagination(newPagination);
    fetchSearchLogs(newPagination);
  };

  const onSuggestionsPageChange = (page, pageSize) => {
    const newPagination = {
      current: page,
      pageSize: pageSize,
      total: searchSuggestionsPagination.total,
    };
    setSearchSuggestionsPagination(newPagination);
    fetchSearchSuggestions(newPagination);
  };

  const handleAddKeyword = async () => {
    if (!newKeyword.trim()) {
      message.warning("Vui lòng nhập từ khóa");
      return;
    }

    try {
      setIsAddingKeyword(true);
      const response = await api.createSearchSuggestion({
        keyword: newKeyword.trim(),
        clickCount: 0,
      });
      if (response.error) {
        throw new Error(response.error.msg);
      }
      message.success("Thêm từ khóa thành công");
      setNewKeyword("");
      // Reset về trang 1 để hiển thị từ khóa vừa thêm
      const resetPagination = {
        current: 1,
        pageSize: searchSuggestionsPagination.pageSize,
        total: 0,
      };
      fetchSearchSuggestions(resetPagination);
    } catch (error) {
      console.error("Error adding keyword:", error);
      message.error("Lỗi khi thêm từ khóa");
    } finally {
      setIsAddingKeyword(false);
    }
  };

  const handleDeleteKeyword = (suggestion) => {
    Modal.confirm({
      title: "Xác nhận xóa",
      content: `Bạn có chắc chắn muốn xóa từ khóa "${suggestion.keyword}"?`,
      okText: "Xóa",
      cancelText: "Hủy",
      okType: "danger",
      onOk: async () => {
        try {
          const response = await api.deleteSearchSuggestion(suggestion._id);
          if (response.error) {
            throw new Error(response.error.msg);
          }
          message.success("Xóa từ khóa thành công");
          // Reset về trang 1 sau khi xóa
          const resetPagination = {
            current: 1,
            pageSize: searchSuggestionsPagination.pageSize,
            total: 0,
          };
          fetchSearchSuggestions(resetPagination);
          // Reload nhật ký tìm kiếm để cập nhật trạng thái
          fetchSearchLogs();
        } catch (error) {
          console.error("Error deleting keyword:", error);
          message.error("Lỗi khi xóa từ khóa");
        }
      },
    });
  };

  const handleDoubleClickEdit = (suggestion) => {
    setEditingId(suggestion._id);
    setEditingValue(suggestion.keyword);
  };

  const handleUpdateKeyword = async () => {
    if (!editingValue.trim()) {
      message.warning("Từ khóa không được để trống");
      return;
    }

    if (
      editingValue.trim() ===
      searchSuggestions.find((s) => s._id === editingId)?.keyword
    ) {
      // Không thay đổi gì, chỉ thoát edit mode
      setEditingId(null);
      setEditingValue("");
      return;
    }

    try {
      setIsUpdatingKeyword(true);
      const response = await api.updateSearchSuggestion(editingId, {
        keyword: editingValue.trim(),
      });
      if (response.error) {
        throw new Error(response.error.msg);
      }
      message.success("Cập nhật từ khóa thành công");
      setEditingId(null);
      setEditingValue("");
      // Reset về trang 1 sau khi cập nhật
      const resetPagination = {
        current: 1,
        pageSize: searchSuggestionsPagination.pageSize,
        total: 0,
      };
      fetchSearchSuggestions(resetPagination);
    } catch (error) {
      console.error("Error updating keyword:", error);
      message.error("Lỗi khi cập nhật từ khóa");
    } finally {
      setIsUpdatingKeyword(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleCreateFromLog = async (log) => {
    // Kiểm tra xem từ khóa đã tồn tại trong suggestions chưa
    const existingSuggestion = searchSuggestions.find(
      (s) => s.keyword.toLowerCase() === log.keyword.toLowerCase()
    );

    if (existingSuggestion) {
      message.warning(
        `Từ khóa "${log.keyword}" đã tồn tại trong danh sách gợi ý`
      );
      return;
    }

    try {
      setCreatingFromLog(log._id);
      const response = await api.createSearchSuggestion({
        keyword: log.keyword,
        clickCount: 0,
      });
      if (response.error) {
        throw new Error(response.error.msg);
      }
      message.success(`Đã thêm "${log.keyword}" vào danh sách gợi ý`);
      // Reset về trang 1 để hiển thị từ khóa vừa thêm
      const resetPagination = {
        current: 1,
        pageSize: searchSuggestionsPagination.pageSize,
        total: 0,
      };
      fetchSearchSuggestions(resetPagination);
      // Reload nhật ký tìm kiếm để cập nhật trạng thái
      fetchSearchLogs();
    } catch (error) {
      console.error("Error creating suggestion from log:", error);
      message.error("Lỗi khi tạo từ khóa gợi ý");
    } finally {
      setCreatingFromLog(null);
    }
  };

  const handleDoubleClickFromLog = (log) => {
    // Kiểm tra xem từ khóa đã tồn tại trong suggestions chưa
    const existingSuggestion = searchSuggestions.find(
      (s) => s.keyword.toLowerCase() === log.keyword.toLowerCase()
    );

    if (existingSuggestion) {
      message.warning(
        `Từ khóa "${log.keyword}" đã tồn tại trong danh sách gợi ý`
      );
      return;
    }

    setEditingFromLogId(log._id);
    setEditingFromLogValue(log.keyword);
  };

  const handleCreateFromLogSubmit = async () => {
    if (!editingFromLogValue.trim()) {
      message.warning("Từ khóa không được để trống");
      return;
    }

    // Kiểm tra duplicate với keyword đã chỉnh sửa
    const existingSuggestion = searchSuggestions.find(
      (s) =>
        s.keyword.toLowerCase() === editingFromLogValue.trim().toLowerCase()
    );

    if (existingSuggestion) {
      message.warning(
        `Từ khóa "${editingFromLogValue.trim()}" đã tồn tại trong danh sách gợi ý`
      );
      return;
    }

    try {
      setIsCreatingFromLog(true);
      const response = await api.createSearchSuggestion({
        keyword: editingFromLogValue.trim(),
        clickCount: 0,
      });
      if (response.error) {
        throw new Error(response.error.msg);
      }
      message.success(
        `Đã thêm "${editingFromLogValue.trim()}" vào danh sách gợi ý`
      );
      setEditingFromLogId(null);
      setEditingFromLogValue("");
      // Reset về trang 1 để hiển thị từ khóa vừa thêm
      const resetPagination = {
        current: 1,
        pageSize: searchSuggestionsPagination.pageSize,
        total: 0,
      };
      fetchSearchSuggestions(resetPagination);
      // Reload nhật ký tìm kiếm để cập nhật trạng thái
      fetchSearchLogs();
    } catch (error) {
      console.error("Error creating suggestion from log:", error);
      message.error("Lỗi khi tạo từ khóa gợi ý");
    } finally {
      setIsCreatingFromLog(false);
    }
  };

  const handleCancelFromLogEdit = () => {
    setEditingFromLogId(null);
    setEditingFromLogValue("");
  };

  const getPopularityColor = (popularity) => {
    const colors = {
      very_high: "volcano",
      high: "orange",
      medium: "gold",
      low: "green",
      very_low: "cyan",
    };
    return colors[popularity] || "blue";
  };

  const getClickCountColor = (clickCount) => {
    if (clickCount >= 100) return "volcano";
    if (clickCount >= 50) return "orange";
    if (clickCount >= 20) return "gold";
    if (clickCount >= 5) return "green";
    return "blue";
  };

  const getClickCountTooltip = (clickCount) => {
    if (clickCount >= 100) return "Rất phổ biến";
    if (clickCount >= 50) return "Phổ biến";
    if (clickCount >= 20) return "Khá phổ biến";
    if (clickCount >= 5) return "Ít phổ biến";
    return "Mới";
  };

  return (
    <>
      {/* Hot Keywords Section */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ marginBottom: "20px" }}>
          <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
            <FireOutlined style={{ marginRight: "8px" }} />
            Từ khóa tìm kiếm nổi bật
          </Title>
          <Text type="secondary">
            Cấu hình các từ khóa hiển thị nổi bật trên ứng dụng
          </Text>
        </div>

        <TextArea
          placeholder="Điền các keyword cách nhau bởi dấu phẩy (,)"
          autoSize={{
            minRows: 4,
            maxRows: 8,
          }}
          value={keywords}
          onChange={onChangeKeywords}
          style={{
            marginBottom: "16px",
            borderRadius: "8px",
            fontSize: "14px",
          }}
        />

        <Button
          type="primary"
          size="large"
          htmlType="submit"
          loading={isLoading}
          onClick={onSubmit}
          style={{
            borderRadius: "8px",
            height: "40px",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          <FireOutlined />
          Cập nhật từ khóa nổi bật
        </Button>
      </div>

      {/* Search Suggestions Section */}
      <div style={{ marginBottom: "40px" }}>
        <div style={{ marginBottom: "20px" }}>
          <Title level={4} style={{ margin: 0, color: "#52c41a" }}>
            <BulbOutlined style={{ marginRight: "8px" }} />
            Từ khóa tìm kiếm gợi ý
          </Title>
          <Text type="secondary">
            Quản lý danh sách từ khóa gợi ý cho người dùng (
            {searchSuggestionsPagination.total} từ khóa)
          </Text>
          <br />
          <Text
            type="secondary"
            style={{ fontSize: "12px", fontStyle: "italic" }}
          >
            💡 Tip: Double click vào từ khóa để chỉnh sửa
          </Text>
        </div>

        {/* Add new keyword */}
        <div
          style={{
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <Text
            strong
            style={{ color: "#52c41a", marginBottom: "12px", display: "block" }}
          >
            Thêm từ khóa mới
          </Text>
          <Input.Group compact style={{ display: "flex" }}>
            <Input
              placeholder="Nhập từ khóa gợi ý mới..."
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onPressEnter={handleAddKeyword}
              style={{
                flex: 1,
                borderRadius: "6px 0 0 6px",
                height: "40px",
              }}
              prefix={<SearchOutlined style={{ color: "#999" }} />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddKeyword}
              loading={isAddingKeyword}
              style={{
                height: "40px",
                borderRadius: "0 6px 6px 0",
                paddingLeft: "20px",
                paddingRight: "20px",
              }}
            >
              Thêm
            </Button>
          </Input.Group>
        </div>

        {/* Keywords list */}
        {isLoadingSuggestions ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              background: "#fafafa",
              borderRadius: "8px",
            }}
          >
            <Text type="secondary">Đang tải...</Text>
          </div>
        ) : (
          <>
            <div
              style={{
                marginBottom: "20px",
                minHeight: "120px",
                padding: "16px",
                background: "#fafafa",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
              }}
            >
              <Space size={[12, 12]} wrap>
                {searchSuggestions.map((suggestion) => (
                  <div key={suggestion._id}>
                    {editingId === suggestion._id ? (
                      // Edit mode
                      <Input
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onPressEnter={handleUpdateKeyword}
                        onBlur={handleCancelEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                        style={{
                          width: "120px",
                          height: "32px",
                          borderRadius: "16px",
                          textAlign: "center",
                        }}
                        autoFocus
                        loading={isUpdatingKeyword}
                        suffix={
                          <Space size={0}>
                            <Button
                              type="text"
                              size="small"
                              onClick={handleUpdateKeyword}
                              loading={isUpdatingKeyword}
                              style={{ padding: "0 4px", height: "20px" }}
                            >
                              ✓
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              onClick={handleCancelEdit}
                              style={{ padding: "0 4px", height: "20px" }}
                            >
                              ✕
                            </Button>
                          </Space>
                        }
                      />
                    ) : (
                      // Display mode
                      <Tooltip
                        title={`${suggestion.clickCount} lượt click • Double click để chỉnh sửa`}
                      >
                        <Tag
                          color={getClickCountColor(suggestion.clickCount)}
                          style={{
                            fontSize: "14px",
                            padding: "6px 12px",
                            borderRadius: "16px",
                            border: "none",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                          }}
                          closable
                          closeIcon={
                            <DeleteOutlined
                              style={{
                                fontSize: "12px",
                                marginLeft: "8px",
                                color: "rgba(0,0,0,0.6)",
                              }}
                            />
                          }
                          onClose={() => handleDeleteKeyword(suggestion)}
                          onDoubleClick={() =>
                            handleDoubleClickEdit(suggestion)
                          }
                        >
                          <span style={{ fontWeight: 500 }}>
                            {suggestion.keyword}
                          </span>
                          <Badge
                            size="small"
                            count={suggestion.clickCount}
                            style={{
                              backgroundColor: "#fff",
                              color: "#333",
                              marginLeft: "8px",
                              boxShadow: "0 0 0 1px #d9d9d9 inset",
                            }}
                          />
                        </Tag>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </Space>
            </div>

            {/* Pagination and actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                background: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <Pagination
                current={searchSuggestionsPagination.current}
                pageSize={searchSuggestionsPagination.pageSize}
                total={searchSuggestionsPagination.total}
                onChange={onSuggestionsPageChange}
                onShowSizeChange={onSuggestionsPageChange}
                showSizeChanger
                showTotal={(total, range) => (
                  <Text type="secondary">
                    {range[0]}-{range[1]} của {total} từ khóa
                  </Text>
                )}
                pageSizeOptions={["10", "20", "50", "100", "200"]}
              />
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={() => fetchSearchSuggestions()}
                loading={isLoadingSuggestions}
                style={{ borderRadius: "6px" }}
              >
                Làm mới
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Search Logs Section */}
      <div>
        <div style={{ marginBottom: "20px" }}>
          <Title level={4} style={{ margin: 0, color: "#722ed1" }}>
            <SearchOutlined style={{ marginRight: "8px" }} />
            Nhật ký tìm kiếm
          </Title>
          <Text type="secondary">
            Theo dõi các từ khóa được tìm kiếm bởi người dùng (
            {searchLogPagination.total} từ khóa)
          </Text>
          <br />
          <Text
            type="secondary"
            style={{ fontSize: "12px", fontStyle: "italic" }}
          >
            💡 Tip: Double click vào từ khóa để chỉnh sửa và thêm vào danh sách
            gợi ý
          </Text>
        </div>

        {isLoadingSearchLogs ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              background: "#fafafa",
              borderRadius: "8px",
            }}
          >
            <Text type="secondary">Đang tải...</Text>
          </div>
        ) : (
          <>
            <div
              style={{
                marginBottom: "20px",
                minHeight: "120px",
                padding: "16px",
                background: "#fafafa",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
              }}
            >
              <Space size={[12, 12]} wrap>
                {searchLogs.map((log) => {
                  // const existsInSuggestions = searchSuggestions.some(
                  //   (s) => s.keyword.toLowerCase() === log.keyword.toLowerCase()
                  // );
                  const isEditing = editingFromLogId === log._id;

                  return (
                    <div key={log._id}>
                      {isEditing ? (
                        // Edit mode for creating from log
                        <Input
                          value={editingFromLogValue}
                          onChange={(e) =>
                            setEditingFromLogValue(e.target.value)
                          }
                          onPressEnter={handleCreateFromLogSubmit}
                          onBlur={handleCancelFromLogEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              handleCancelFromLogEdit();
                            }
                          }}
                          style={{
                            width: "140px",
                            height: "32px",
                            borderRadius: "16px",
                            textAlign: "center",
                          }}
                          autoFocus
                          loading={isCreatingFromLog}
                          suffix={
                            <Space size={0}>
                              <Button
                                type="text"
                                size="small"
                                onClick={handleCreateFromLogSubmit}
                                loading={isCreatingFromLog}
                                style={{ padding: "0 4px", height: "20px" }}
                              >
                                ✓
                              </Button>
                              <Button
                                type="text"
                                size="small"
                                onClick={handleCancelFromLogEdit}
                                style={{ padding: "0 4px", height: "20px" }}
                              >
                                ✕
                              </Button>
                            </Space>
                          }
                        />
                      ) : (
                        // Display mode
                        <Tooltip
                          title={
                            log.isSuggestion
                              ? "Từ khóa này đã có trong danh sách gợi ý"
                              : "Double click để chỉnh sửa và thêm vào danh sách gợi ý"
                          }
                        >
                          <Tag
                            color={getPopularityColor(log.popularity)}
                            style={{
                              fontSize: "14px",
                              padding: "6px 12px",
                              borderRadius: "16px",
                              border: "none",
                              cursor: log.isSuggestion ? "default" : "pointer",
                              opacity: log.isSuggestion ? 0.6 : 1,
                              transition: "all 0.3s ease",
                            }}
                            onDoubleClick={
                              log.isSuggestion
                                ? undefined
                                : () => handleDoubleClickFromLog(log)
                            }
                          >
                            <span style={{ fontWeight: 500 }}>
                              {log.keyword}
                              {log.isSuggestion && " ✓"}
                            </span>
                            <Badge
                              size="small"
                              count={log.searchCount}
                              style={{
                                backgroundColor: "#fff",
                                color: "#333",
                                marginLeft: "8px",
                                boxShadow: "0 0 0 1px #d9d9d9 inset",
                              }}
                            />
                          </Tag>
                        </Tooltip>
                      )}
                    </div>
                  );
                })}
              </Space>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                background: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <Pagination
                current={searchLogPagination.current}
                pageSize={searchLogPagination.pageSize}
                total={searchLogPagination.total}
                onChange={onSearchLogPageChange}
                onShowSizeChange={onSearchLogPageChange}
                showSizeChanger
                showTotal={(total, range) => (
                  <Text type="secondary">
                    {range[0]}-{range[1]} của {total} từ khóa
                  </Text>
                )}
                pageSizeOptions={["10", "20", "50", "100", "200", "300", "500"]}
              />
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={() => fetchSearchLogs()}
                loading={isLoadingSearchLogs}
                style={{ borderRadius: "6px" }}
              >
                Làm mới
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
